import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LoggingErrorBoundary, LoggerProvider } from '@shared-ui/logging';
import { ApiProvider } from '@shared-ui/api';
import { TenantGate, currentTenantPrefix, tenantTokenKey } from '@shared-ui/tenant';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from '@shared-ui/theme';
import App from './App';
import { API_BASE } from './api/config';
import { captureArrival } from './api/auth';
import { reportLinkLanding } from '@shared-ui/acquisition';
import { reloadOnStaleChunk } from '@shared-ui/app-pages';
import '@shared-ui/theme/index.css';

// Where this browser came from, read from the landing URL before anything
// renders or navigates — the referrer and the ad tags exist only here, now.
captureArrival();

// A published-link landing: how long the follower waited for the first paint,
// on the browser's clock from the tap — the bundle download included.
reportLinkLanding(API_BASE, 'student');

// A tab open across a deploy asks for chunks that are gone; reload into the new build.
reloadOnStaleChunk();

// Suppress benign ResizeObserver loop warning (Fabric.js canvas, MathJax rendering)
window.addEventListener('error', (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    e.stopImmediatePropagation();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Tenant-scoped on the path form, where every /t/<slug> shares one origin and
// so one localStorage. Plain `token` everywhere else. See @shared-ui/tenant.
const TOKEN_KEY = tenantTokenKey('token');
const getAuthToken = () => localStorage.getItem(TOKEN_KEY);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <LoggingErrorBoundary>
      <LoggerProvider appSource="web-student" apiBaseUrl={API_BASE} getAuthToken={getAuthToken}>
        <ApiProvider tokenKey={TOKEN_KEY}>
          <TenantGate>
          <BrowserRouter basename={currentTenantPrefix().basename}>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <ThemeProvider>
                  <App />
                </ThemeProvider>
              </AuthProvider>
            </QueryClientProvider>
          </BrowserRouter>
          </TenantGate>
        </ApiProvider>
      </LoggerProvider>
    </LoggingErrorBoundary>
  </StrictMode>
);
